import React from 'react';

import { typographyScale, TypographyVariant } from '@/styles/typography.tokens';
import { createLogger } from '@/utils/logging';

const logger = createLogger('Text', 'component');

type AnyElement = keyof React.JSX.IntrinsicElements;

const defaultTag: Record<TypographyVariant, AnyElement> = {
  display: 'h1',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  bodyLarge: 'p',
  bodyDefault: 'p',
  bodySmall: 'p',
  bodyLargeStrong: 'p',
  bodyDefaultStrong: 'p',
  buttonLarge: 'span',
  buttonDefault: 'span',
  buttonSmall: 'span',
  labelUI: 'label',
  caption: 'span',
  overline: 'span',
};

export interface TextProps extends React.HTMLAttributes<HTMLElement> {
  /** Type scale variant — drives font family, weight, size, and line-height. */
  variant: TypographyVariant;
  /** Override the rendered HTML element. Default is inferred from variant. */
  as?: AnyElement;
  /**
   * Text color. Accepts any CSS value or `var(--token)` reference.
   * Omitting this prop inherits color from context — no color is hardcoded.
   */
  color?: string;
}

/**
 * Renders any type scale variant with the correct font, weight, size, and
 * line-height from the Blend typography token system.
 *
 * Colors are intentionally not baked in. Pass `color="var(--text-primary)"`
 * or any CSS color string; omit to inherit from the surrounding context.
 */
export const Text: React.FC<TextProps> = ({ variant, as, color, style, children, ...rest }) => {
  const token = typographyScale[variant];

  if (!token) {
    logger.error(`Invalid typography variant: "${variant}". Check Text component usage.`);
    return <span>{children}</span>;
  }

  const Tag = (as ?? defaultTag[variant]) as React.ElementType;

  return (
    <Tag
      style={{
        fontFamily: token.fontFamily,
        fontWeight: token.fontWeight,
        fontSize: token.fontSize,
        lineHeight: token.lineHeight,
        letterSpacing: token.letterSpacing,
        margin: 0,
        padding: 0,
        ...(color === undefined ? {} : { color }),
        ...style,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Text;
